import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import { getNavigationData } from './navigation.js';

const { PromotionUtils } = MojitoModules.Promotions;
const SelectionButton = MojitoModules.SelectionButton.module;

const {
    Button,
    DateTime,
    FlexPane,
    Text,
    GridLayout: { GridPane },
} = MojitoPresentation.Components;
const { Types: IntentTypes } = MojitoPresentation.Base.Intent;
const { getSeparatorTranslationKey } = MojitoPresentation.Utils.ParticipantSeparator;
const { sortParticipants } = MojitoPresentation.Components.scoreboardUtils;
const { dispatch } = MojitoCore.Services.redux.store;
const intentActions = MojitoCore.Intents.actions;
const EventUtils = MojitoServices.SportsContent.Events.utils;

const getEventName = (event, separator) => {
    if (event.participants?.length) {
        return sortParticipants(event.participants, event.homeAway)
            .map(participant => participant.name)
            .join(separator);
    }
    return event.name;
};

// Outright events list one participant per entrant, so the market name is shown instead.
const getPrimaryLabel = (event, market, isOutright, separator) =>
    isOutright ? market.name || event.name : getEventName(event, separator);

const getVisibleSelections = (market, isOutright, maxSelectionsAmount) => {
    const selections = market.selections || [];

    if (!maxSelectionsAmount || selections.length <= maxSelectionsAmount) {
        return selections;
    }

    return isOutright
        ? PromotionUtils.getTopSelections(market, maxSelectionsAmount)
        : selections.slice(0, maxSelectionsAmount);
};

const SearchEventItem = props => {
    const {
        event,
        market,
        isSearchResult,
        onNavigate,
        mojitoTools: { appContext, config, stringResolver },
    } = props;
    const selections = market.selections || [];
    const { maxSelectionsAmount } = config;
    const isOutright = EventUtils.isOutrightEventType(event.eventType);
    const visibleSelections = getVisibleSelections(market, isOutright, maxSelectionsAmount);
    const showAllButton = maxSelectionsAmount > 0 && selections.length > maxSelectionsAmount;
    const navigationData = getNavigationData(event);
    const primaryLabel = getPrimaryLabel(
        event,
        market,
        isOutright,
        ` ${stringResolver.resolveString(getSeparatorTranslationKey(event.homeAway))} `
    );

    const onEventClick = () => {
        dispatch(intentActions.publishIntent(IntentTypes.VIEW, navigationData));
        if (isSearchResult) {
            appContext.analyticsEmitter.emitAnalytics('searchResultsItemClicked', {
                sportId: event.sportId,
                name: event.name,
                parentName: event.parentName,
                resultType: event.resultType,
            });
        }
        onNavigate();
    };

    return (
        <FlexPane config={config.container} class="ta-SearchEventItem">
            <Button
                config={config.eventButton}
                onClick={onEventClick}
                hrefLink={appContext.routeResolver.getRoute(navigationData)}
                class="ta-searchEventButton"
            >
                <FlexPane config={config.eventHeader}>
                    <Text config={config.competitionName}>
                        {event.typeName || event.parentName}
                    </Text>
                    {event.startTime && (
                        <Text config={config.startTime}>
                            <DateTime config={config.startTimeFormat} dateTime={event.startTime} />
                        </Text>
                    )}
                </FlexPane>
            </Button>
            <FlexPane config={config.eventNameRow}>
                <Button
                    config={config.eventNameButton}
                    onClick={onEventClick}
                    hrefLink={appContext.routeResolver.getRoute(navigationData)}
                    class="ta-searchEventName"
                >
                    <Text config={config.eventName}>{primaryLabel}</Text>
                </Button>
                {showAllButton && (
                    <Button
                        config={config.showAllButton}
                        onClick={onEventClick}
                        hrefLink={appContext.routeResolver.getRoute(navigationData)}
                        class="ta-searchEventShowAll"
                    >
                        <Text config={config.showAllLabel}>
                            {stringResolver.resolveAndFormatString(
                                '$SEARCH_DISCOVERY_VIEW.SHOW_ALL_SELECTIONS',
                                selections.length
                            )}
                        </Text>
                    </Button>
                )}
            </FlexPane>
            {visibleSelections.length > 0 && (
                <GridPane
                    repeatedColumnsNum={visibleSelections.length}
                    config={config.selectionsContainer}
                >
                    {visibleSelections.map(selection => (
                        <SelectionButton
                            key={selection.id}
                            config={config.selectionButton}
                            selection={selection}
                            event={event}
                            marketStatus={market.status}
                            marketType={market.type}
                            marketId={market.id}
                        />
                    ))}
                </GridPane>
            )}
        </FlexPane>
    );
};

export default SearchEventItem;
